import { get } from "lodash";

import { getSettings } from "@/pages/admin/Settings/settings.api";
import { isResourceApplicable } from "./applicability";
import { inspectOwnedResource } from "./results";
import type { ResourceDriver, TenancySettingResourceDefinition } from "./types";

/** The tenancy's settings, loaded once per tenancy for every tenancy setting the setup declares. */
export interface TenancySettingValue {
  id: string;
  /** The setting's current value. */
  current: unknown;
  settings: Record<string, unknown>;
}

export interface TenancySettingsDocument {
  id: string;
  settings: Record<string, unknown>;
}

/**
 * A tenancy that has never saved its settings has none yet; its settings are the defaults. The client doesn't
 * always throw `Error` instances, so read the message off whatever it throws (as Admin → Settings does).
 */
const isMissingSettingsError = (error: unknown): boolean => {
  const message = (error as { message?: unknown } | null | undefined)?.message;
  return typeof message === "string" && message.toLowerCase().includes("missing");
};

export const loadTenancySettings = async (tenancyId: string): Promise<TenancySettingsDocument[]> => {
  try {
    const response = await getSettings.submit({ params: { id: tenancyId } });
    const settings = (response as { data?: unknown } | undefined)?.data ?? response;
    return [{ id: tenancyId, settings: settings && typeof settings === "object" ? (settings as Record<string, unknown>) : {} }];
  } catch (error) {
    if (isMissingSettingsError(error)) return [{ id: tenancyId, settings: {} }];
    throw error;
  }
};

export const tenancySettingResourceDriver: ResourceDriver<TenancySettingResourceDefinition, "tenancySetting"> = {
  kind: "tenancySetting",
  setup: "manual",
  collection: {
    scopeId: ({ tenancyId }) => tenancyId,
    load: loadTenancySettings,
  },
  describe: (declaration) => declaration.resource.name,
  define: (context, owner, declaration) => [{
    key: `${owner.stepId}:${declaration.resource.key}`,
    stepId: owner.stepId,
    definitionKey: declaration.resource.key,
    kind: "tenancySetting",
    label: declaration.resource.name,
    applicable: isResourceApplicable(context.answers, owner, declaration.resource),
    resource: declaration.resource,
  }],
  inspect: (context, definition) => {
    const document = (context.collections?.tenancySetting as TenancySettingsDocument[] | undefined)?.[0];
    const { name, description, path, value } = definition.resource;
    const complete = Boolean(document) && get(document?.settings, path) === value;
    return inspectOwnedResource(definition, {
      // The whole settings document travels with the inspection so a block can save the setting's section.
      found: document ? { id: definition.resource.key, current: get(document.settings, path), settings: document.settings } : undefined,
      inputs: [{ id: definition.resource.key, title: name, instructions: description, complete }],
      complete,
      messages: {
        missing: `Turn on ${name} in Admin → Settings.`,
        incomplete: `Turn on ${name}.`,
        ready: `${name} is on.`,
      },
      // No href: a setting is changed in the step itself, and an href would send auto-follow off the build pages.
    });
  },
};
